import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { MobileDeviceType } from './mobile-device-type.type';

@Injectable({ providedIn: 'root' })
export class CheckDeviceService {
  private deviceTypeSubject = new BehaviorSubject<MobileDeviceType>(this.detectDeviceType());
  public deviceType$ = this.deviceTypeSubject.asObservable();

  private isMobileSubject = new BehaviorSubject<boolean>(this.deviceTypeSubject.getValue() !== 'unknown');
  public isMobile$ = this.isMobileSubject.asObservable();

  private dismissedSubject = new BehaviorSubject<boolean>(false);
  public dismissed$ = this.dismissedSubject.asObservable();

  public shouldShowBanner$ = combineLatest([this.isMobile$, this.dismissed$]).pipe(
    map(([isMobile, dismissed]) => isMobile && !dismissed),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor() {
    if (typeof window !== 'undefined') {
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(200),
          map(() => this.detectDeviceType())
        )
        .subscribe(newDeviceType => {
          if (newDeviceType !== this.deviceTypeSubject.getValue()) {
            this.deviceTypeSubject.next(newDeviceType);
            this.isMobileSubject.next(newDeviceType !== 'unknown');
          }
        });
    }
  }

  public dismiss(): void {
    if (!this.dismissedSubject.value) {
      this.dismissedSubject.next(true);
    }
  }

  public resetDismissed(): void {
    this.dismissedSubject.next(false);
  }

  private getUserAgent(): string {
    return navigator.userAgent.toLowerCase();
  }

  private detectDeviceType(): MobileDeviceType {
    const ua = this.getUserAgent();

    if (/android/.test(ua)) return 'android';
    if (/iphone|ipod|ipad/.test(ua)) return 'ios';

    const isModernIpad = navigator.userAgent.includes('Mac') && 'ontouchend' in document;

    if (isModernIpad) return 'ios';

    return 'unknown';
  }
}
